import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import type { PercentCrop } from 'react-image-crop';
import { Box, ButtonV2, Divider, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Add, Crop as IconCrop, Import, Trash } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { DragIcon } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { addRendition } from '@shared/utils/photoRendition';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import SortableList from '../SortableList';
import { useSortableItemContext } from '../SortableList/context/SortableItem.context';
import type { MultiPhotoSelectorProps, Photo, PhotoControlCardProps } from './MultiPhotoSelector.types';
import { getPhotoAspectRatioByConfig } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/utils/websiteHeaderSettings.utils';
import { CroppedImage } from './components/CroppedImage/CroppedImage';
import type { AspectRatio } from '@shared/utils/photoPresentationLayout';
import { findAspectRatioFromPercentCrop } from '@shared/utils/findAspectRatioFromPercentCrop';
import { TooltipV2Wrapper } from '../TooltipV2Wrapper';

const CARD_HEIGHT = 114;

const PhotoControlCard: FC<PhotoControlCardProps> = props => {
  const {
    photoId,
    photoUrl,
    handleDelete,
    handleSwap,
    handlePhotoAdjust,
    hideDelete,
    isLast,
    isDragActive,
    isDisabledDrag,
    dragItemId,
    index,
    isPhotoAdjustEnabled,
    cropInfo,
    adjustmentMode,
    aspectRatio,
    cardBgColor,
    showCropTooltip,
    onCropTooltipDismiss,
  } = props;

  const { tq } = useTranslation('joykit');

  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();

  return (
    <Flex
      width="100%"
      flexDirection="column"
      gap={4}
    >
      <Flex
        width="100%"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor={cardBgColor}
        gap={3}
      >
        <Flex
          flexDirection="row"
          gap={3}
          alignItems="center"
        >
          <Flex
            display={isDisabledDrag ? 'none' : 'flex'}
            cursor={isDragActive ? 'grabbing' : 'grab'}
            {...attributes}
            {...listeners}
            ref={draggableNodeRef}
          >
            <DragIcon />
          </Flex>
          {adjustmentMode === 'crop' && cropInfo ? (
            <Box
              as="button"
              type="button"
              aria-label={tq`Swap Photo`}
              onClick={() => handleSwap(photoId)}
              padding={0}
              border="none"
              cursor="pointer"
              width={aspectRatio ? pxToRem(aspectRatio * 101) : pxToRem(143)}
              maxWidth={{ _: pxToRem(130), xs: 'none' }}
              height={pxToRem(101)}
              overflow="hidden"
              borderRadius={3}
            >
              <CroppedImage
                imageUrl={photoUrl}
                cropInfo={cropInfo}
                width={aspectRatio ? aspectRatio * 101 : 143}
                height={101}
              />
            </Box>
          ) : (
            <Box
              as="button"
              type="button"
              aria-label={tq`Swap Photo`}
              onClick={() => handleSwap(photoId)}
              padding={0}
              border="none"
              cursor="pointer"
              width={pxToRem(143)}
              height={pxToRem(101)}
              overflow="hidden"
              borderRadius={3}
              background={`url("${photoUrl}") lightgray 50%`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
            />
          )}
        </Flex>
        <Flex
          flexDirection="row"
          gap={3}
        >
          <TooltipV2
            content={<TextV2 typographyVariant="label3">{tq`Swap Photo`}</TextV2>}
            onMouseEnterDelay={500}
          >
            <Flex
              as="button"
              onClick={() => handleSwap(photoId)}
              border="1px solid"
              borderColor="mono3"
              borderRadius={pxToRem(20)}
              width={pxToRem(40)}
              height={pxToRem(40)}
              alignItems="center"
              justifyContent="center"
              backgroundColor="white"
              color="mono14"
              _hover={{ color: 'mono12' }}
              _active={{ color: 'mono13' }}
            >
              <Import
                size="sm"
                pointerEvents="none"
              />
            </Flex>
          </TooltipV2>
          {isPhotoAdjustEnabled && (
            <TooltipV2
              content={<TextV2 typographyVariant="label3">{adjustmentMode === 'focusPoint' ? tq`Adjust Photo` : tq`Crop Photo`}</TextV2>}
              onMouseEnterDelay={500}
              {...(showCropTooltip === true ? { isOpen: true, onClose: onCropTooltipDismiss } : {})}
            >
              <Flex
                as="button"
                onClick={() => handlePhotoAdjust(photoId, index)}
                border="1px solid"
                borderColor="mono3"
                borderRadius={pxToRem(20)}
                width={pxToRem(40)}
                height={pxToRem(40)}
                alignItems="center"
                justifyContent="center"
                backgroundColor="white"
                color="mono14"
                _hover={{ color: 'mono12' }}
                _active={{ color: 'mono13' }}
              >
                <IconCrop
                  size="sm"
                  pointerEvents="none"
                />
              </Flex>
            </TooltipV2>
          )}
          {!hideDelete && (
            <TooltipV2
              content={<TextV2 typographyVariant="label3">{tq`Remove Photo`}</TextV2>}
              onMouseEnterDelay={500}
            >
              <Flex
                as="button"
                onClick={() => handleDelete(photoId)}
                border="1px solid"
                borderColor="mono3"
                borderRadius={pxToRem(20)}
                width={pxToRem(40)}
                height={pxToRem(40)}
                alignItems="center"
                justifyContent="center"
                backgroundColor="white"
                color="mono14"
                _hover={{ color: 'negative5' }}
                _active={{ color: 'negative7' }}
              >
                <Trash
                  size="sm"
                  pointerEvents="none"
                />
              </Flex>
            </TooltipV2>
          )}
        </Flex>
      </Flex>
      {!isLast && (
        <Divider
          key={`divider-${photoId}`}
          marginX="auto"
          width="100%"
          borderColor={'mono3'}
          visibility={dragItemId === photoId ? 'hidden' : 'visible'}
        />
      )}
    </Flex>
  );
};

export const MultiPhotoSelector = <T extends Photo>(props: MultiPhotoSelectorProps<T>) => {
  const {
    min,
    max,
    photoData,
    handleOnChange,
    handlePhotoSwap,
    handlePhotoAdjust,
    handlePhotoDelete,
    onDragEnd,
    onAddPhotosClick,
    aspectRatio,
    enablePhotoAdjust = false,
    photoAdjustmentMode,
    isDisabledDrag = false,
    cardBgColor,
    showCropTooltip,
    onCropTooltipDismiss,
  } = props;

  const [dragItemId, setDragItemId] = useState<string | null>(null);

  const { tq } = useTranslation('joykit');

  const handleDragStart = useEventCallback((activeId: string) => {
    setDragItemId(activeId);
  });

  const handleDragCancel = useEventCallback(() => {
    setDragItemId(null);
  });

  const handleDragEnd = useEventCallback((dragData: { fromIndex: number; toIndex: number; items: Photo[]; reorderedItem: Photo | undefined }) => {
    setDragItemId(null);
    const hasSortOrderChanged = !dragData.items.every((item, index) => item.id === photoData[index].id);
    // If the sort order has changed, update the sort order in the backend
    if (hasSortOrderChanged) {
      const formattedItemIds = dragData.items.map(itemData => itemData.id);
      onDragEnd(formattedItemIds);
    }
  });

  const onPhotoAdjustClick = useEventCallback((photoId: string, index: number) => {
    const foundPhoto = photoData.find(photo => photo.id === photoId);
    if (!foundPhoto) return;

    handlePhotoAdjust?.(photoId, index);
  });

  const computeAspectRatio = useCallback((aspectRatio: AspectRatio | undefined, index: number, cropInfo?: PercentCrop, height?: number, width?: number) => {
    let ratio: number | undefined = undefined;
    if (aspectRatio && aspectRatio !== 'locked') {
      ratio = getPhotoAspectRatioByConfig(aspectRatio, index) as number;
    } else if (aspectRatio === null && cropInfo && width && height) {
      // aspectRatio=null means, we allow freeform cropping. So we need to find the aspect ratio from cropinfo for header that doesn't have a fixed aspect ratio
      ratio = findAspectRatioFromPercentCrop(cropInfo, width, height);
    }
    return ratio;
  }, []);

  const onClickAddPhoto = useEventCallback(() => {
    if (photoData.length >= max) return;
    onAddPhotosClick();
  });

  return (
    <>
      <SortableList
        items={photoData as T[]}
        renderItem={(item, index) => {
          return (
            <PhotoControlCard
              key={item.id}
              photoId={item.id}
              photoUrl={addRendition({ url: item.url, renditionSize: 'medium' })}
              handleDelete={handlePhotoDelete}
              handleSwap={handlePhotoSwap}
              handlePhotoAdjust={onPhotoAdjustClick}
              hideDelete={photoData.length <= min}
              isLast={index === photoData.length - 1}
              isDragActive={!!dragItemId}
              isDisabledDrag={isDisabledDrag}
              dragItemId={dragItemId}
              index={index}
              isPhotoAdjustEnabled={enablePhotoAdjust}
              cropInfo={item.cropInfo}
              adjustmentMode={photoAdjustmentMode}
              aspectRatio={computeAspectRatio(aspectRatio, index, item.cropInfo, item.height, item.width)}
              cardBgColor={cardBgColor}
              showCropTooltip={index === 0 ? showCropTooltip : undefined}
              onCropTooltipDismiss={index === 0 ? onCropTooltipDismiss : undefined}
            />
          );
        }}
        onChange={handleOnChange}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
        renderVirtualList={false}
        itemSize={CARD_HEIGHT}
      />
      <TooltipV2Wrapper
        disabled={photoData.length < max}
        onMouseEnterDelay={500}
        content={<TextV2 typographyVariant="label2">{tq`The maximum number of photos have been selected for this header layout`}</TextV2>}
      >
        <ButtonV2
          display={max === 0 ? 'none' : 'inline-flex'}
          variant="link"
          size="sm"
          shape="rounded"
          intent="neutral"
          width="fit-content"
          color={photoData.length >= max ? 'mono8' : 'newUIBrandPrimary'}
          onClick={onClickAddPhoto}
          cursor={photoData.length >= max ? 'not-allowed' : 'pointer'}
          marginTop={photoData.length > 0 ? 4 : 0}
          startIcon={<Add size={16} />}
          _hover={{
            textDecoration: 'none',
            color: photoData.length >= max ? 'mono8' : 'mono12',
          }}
        >
          <TextV2
            typographyVariant="label6"
            fontSize={pxToRem(14)}
            textDecorationStyle="dotted"
            textDecorationThickness="auto"
            textDecorationLine="underline"
            css={{
              textUnderlineOffset: '25%',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
            }}
          >
            {tq`Add Photo`}
          </TextV2>
        </ButtonV2>
      </TooltipV2Wrapper>
    </>
  );
};

MultiPhotoSelector.displayName = 'MultiPhotoSelector';
